import React, { useState } from "react";
import SectionHeading from "../components/SectionHeading";
import CtaBand from "../components/CtaBand";
import TechBackdrop from "../components/TechBackdrop";
import { supportPlans, faqs } from "../mock";
import { Check, ShieldCheck, Headphones, Activity, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";

function Accordion({ items }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      {items.map((it, i) => {
        const isOpen = open === i;
        return (
          <div key={i} className="border-b border-slate-100 last:border-b-0">
            <button
              onClick={() => setOpen(isOpen ? -1 : i)}
              className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-slate-50 transition-colors"
            >
              <span className="font-display text-[17px] font-semibold text-slate-900">{it.q}</span>
              <ChevronDown className={`h-5 w-5 text-slate-500 flex-shrink-0 transition-transform ${isOpen ? "rotate-180 text-brand-700" : ""}`} />
            </button>
            <div className={`grid transition-all duration-300 ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
              <div className="overflow-hidden">
                <div className="px-5 pb-5 text-[15px] leading-relaxed text-slate-600">{it.a}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function SupportPage() {
  return (
    <>
      <section className="relative pt-36 lg:pt-44 pb-12 overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-60" />
        <TechBackdrop variant="topology" opacity={0.07} className="hidden lg:block" />
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <SectionHeading
            eyebrow="Managed support"
            title="Keep your Power estate humming."
            description="Three tiered support plans designed around how critical your AIX workloads are. Proactive monitoring, named engineers, and SLA-backed response times."
          />
        </div>
      </section>

      <section className="pb-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: ShieldCheck, t: "SLA-backed response", d: "From 15 min to 4 h depending on tier." },
            { icon: Activity, t: "Proactive monitoring", d: "nmon + custom agents on every LPAR." },
            { icon: Headphones, t: "Named engineers", d: "Same people every time — no rotating bench." },
            { icon: Clock, t: "Transparent reporting", d: "Monthly service review & incident trend report." }
          ].map((v, i) => (
            <div key={i} className="rounded-xl border border-slate-200 bg-white p-5 flex gap-4 hover-lift">
              <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-700 grid place-items-center flex-shrink-0">
                <v.icon className="h-5 w-5" />
              </div>
              <div>
                <div className="font-semibold text-slate-900 text-[14px]">{v.t}</div>
                <div className="text-[13px] text-slate-600 mt-0.5">{v.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-5">
            {supportPlans.map((p) => (
              <div
                key={p.name}
                className={`relative rounded-3xl p-7 lg:p-8 ${
                  p.highlight
                    ? "bg-slate-950 text-white shadow-2xl shadow-brand-900/20"
                    : "bg-white border border-slate-200"
                }`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-brand-400 text-slate-950 text-[11px] font-bold tracking-wide uppercase">
                    Most popular
                  </div>
                )}
                <div className={`font-display text-lg font-semibold ${p.highlight ? "text-brand-300" : "text-brand-700"}`}>{p.name}</div>
                <div className="mt-2 flex items-baseline gap-1">
                  <div className={`font-display text-3xl lg:text-4xl font-bold ${p.highlight ? "text-white" : "text-slate-900"}`}>
                    {p.price}
                  </div>
                  {p.cycle && <div className={`text-[13px] ${p.highlight ? "text-slate-400" : "text-slate-500"}`}>{p.cycle}</div>}
                </div>
                <p className={`mt-3 text-[14px] ${p.highlight ? "text-slate-300" : "text-slate-600"}`}>{p.description}</p>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-[14px]">
                      <Check className={`h-4 w-4 mt-0.5 flex-shrink-0 ${p.highlight ? "text-brand-300" : "text-brand-600"}`} />
                      <span className={p.highlight ? "text-slate-200" : "text-slate-700"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className="block mt-7">
                  <Button className={`w-full rounded-full h-11 ${
                    p.highlight
                      ? "bg-brand-400 hover:bg-brand-300 text-slate-950 font-semibold"
                      : "bg-slate-900 hover:bg-slate-800 text-white"
                  }`}>
                    Talk to us
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <SectionHeading eyebrow="FAQ" title="Answers to common questions." />
          <div className="mt-10">
            <Accordion items={faqs} />
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
